(ns app.front.view.inspections.summary-list
  (:require [app.domain.inspection.model :as inspection-model]
            [app.domain.util :as du]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.ui.table :as ui-table]
            [app.front.view.inspections.subs :as inspection-sub]
            [clojure.string :as str]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub]]
            [core.front.href :as href]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]))

(def has-events?
  (comp seq
        (partial filter (some-fn inspection-model/is-visit?
                                 (du/in-state-fn :booking.state/dispatched
                                                 :booking.state/to-dispatch)))
        :entity/_parent))

(defn last-passed? [inspection]
  (->> inspection
       :entity/_parent
       (filter inspection-model/is-visit?)
       (sort-by :inspection.visit/start-time)
       last
       :inspection.visit/result
       (= :inspection.visit.result/passed)))

(rf/reg-sub ::all-inspections
  (fn [[_ workspace-id]]
    (rf/subscribe [::entities/entity {:workspace/id workspace-id}]))
  :-> workspace-model/inspections)


(rf/reg-sub ::inspections-passed+started+upcoming
  (fn [[_ workspace-id]]
    (rf/subscribe [::all-inspections workspace-id]))
  (fn [inspections _]
    (let [[not-started w-events] (->> inspections
                                      (sort-by :ordered/order)
                                      reverse
                                      (split-with (complement has-events?)))]
      (conj (->> w-events
                 reverse
                 (split-with last-passed?)
                 vec)
            (reverse not-started)))))

(rf/reg-sub ::passed-inspections-hidden-count
  (fn [[_ workspace-id]]
    (rf/subscribe [::inspections-passed+started+upcoming workspace-id]))
  (fn [[passed] _]
    (-> passed count dec)))

(rf/reg-sub ::not-started-inspections-hidden-count
  (fn [[_ workspace-id]]
    (rf/subscribe [::inspections-passed+started+upcoming workspace-id]))
  (fn [[_ _ not-started-inspections] _]
    (-> not-started-inspections count dec)))

;; returns the
;;  * last passed inspection
;;  * inspections up to the last inspection that
;;    have events (bookings or visits)
(rf/reg-sub ::inspections
  (fn [[_ workspace-id]]
    (rf/subscribe [::inspections-passed+started+upcoming workspace-id]))
  (fn [[passed started upcoming] _]
    (->> (concat [(last passed)] started [(first upcoming)])
         (filter some?))))


(defn hidden-help [{:keys [tr workspace-id]}]
  (let [hidden-count        (<sub [::passed-inspections-hidden-count workspace-id])
        hidden-future-count (<sub [::not-started-inspections-hidden-count workspace-id])]
    [:div.pb-2
     (->> [(when (> hidden-count 0)
             (tr [:inspection/hidden-passed-inspections] [hidden-count]))
           (when (> hidden-future-count 0)
             (tr [:inspection/hidden-future-inspections] [hidden-future-count]))]
          (filter some?)
          (str/join ", "))]))

(defn inspection-status [tr kind visits-conf? inspection]
  (let [events             (:entity/_parent inspection)
        visits             (->> events
                                (filter inspection-model/is-visit?))
        last-visit         (->> visits
                                (sort-by :inspection.visit/start-time)
                                last)
        last-visit-state   (:domain/state last-visit)
        last-result        (if visits-conf? (:inspection.visit/visit-result last-visit)
                               (:inspection.visit/result last-visit))
        wide?              (= kind :wide)
        requested-booking? (->> events
                                (filter (du/in-state-fn :booking.state/to-dispatch))
                                seq)
        confirmed-booking? (->> events
                                (filter (du/in-state-fn :booking.state/dispatched))
                                seq)
        booking-state-str  (when (and wide?
                                      (or requested-booking? confirmed-booking?))
                             (str " ("
                                  (if confirmed-booking?
                                    (tr [:booking/confirmed])
                                    (tr [:booking/requested]))
                                  ")"))
        lang               (<sub [:language])]
    (when (seq events)
      (cond
        ;; a completed visit
        (= last-visit-state :inspection.visit.state/done)
        [:<>
         (if visits-conf?
           [:span {:class (when (inspection-model/failed-result? last-result)
                            "bold")}
            (inspection-model/visit-result-name last-result lang)]
           [:span {:class (when (= :inspection.visit.result/failed last-result)
                            "bold")}
            (tr [last-result])])
         [:span booking-state-str]]

        ;; has a visit
        last-visit-state
        [:span (str (tr [last-visit-state]))]

        ;; has a requested booking
        requested-booking?
        [:span (tr [:booking/requested])]

        ;; has a confirmed booking
        confirmed-booking?
        [:span (tr [:booking/confirmed])]

        :else
        nil))))

(defn inspection-visits [inspection]
  (->> inspection
       :entity/_parent
       (filter inspection-model/is-visit?)))

(defn count-if-some [things]
  (when (seq things)
    (count things)))

(defn last-visit [inspection]
  (let [events          (:entity/_parent inspection)
        visits          (->> events
                             (filter inspection-model/is-visit?))
        last-visit      (->> visits
                             (sort-by :inspection.visit/start-time)
                             last)
        current-booking (->> events
                             (filter (du/in-state-fn :booking.state/dispatched))
                             (sort-by :booking/actual-start-time)
                             last)
        workspace       (-> inspection
                            :entity/parent
                            :entity/parent)]
    (or (date-util/date-time-in-tz
          (:booking/actual-start-time current-booking)
          (-> workspace :domain/authority-group :group/tz))
        ((<sub [::date-util/date-fn])
         (:inspection.visit/start-time last-visit)))))

(defn inspection-href [inspection]
  (href/href :view/inspection-visit
             {:workspace-id  (-> inspection
                                 :entity/parent
                                 :entity/parent
                                 :workspace/id)
              :inspection-id (:db/id inspection)}))

(def result->status
  {:inspection.visit.result/failed :critical
   :inspection.visit.result/passed :ok})

(defn visit-result->status [{:inspection-visit-result/keys [status-type]}]
  (case status-type
    :inspection.visit.result-status-type/failed :critical
    :inspection.visit.result-status-type/passed :ok
    nil))

(defn get-status [visits-conf? visits]
  (let [last-visit       (->> visits
                              (sort-by :inspection.visit/start-time)
                              last)
        last-visit-state (:domain/state last-visit)]
    (when (= last-visit-state :inspection.visit.state/done)
      (if visits-conf?
        (-> last-visit :inspection.visit/visit-result visit-result->status)
        (-> last-visit :inspection.visit/result result->status)))))

(defn get-focus? [highlight-inspection-id inspection]
  (= highlight-inspection-id (:db/id inspection)))

(defn summary-list
  "Inlude at least `:tr` and `:workspace-id`.
  Feel free to define `:booking` in opts to highlight current
  `:kind` `:wide` adds more cols"
  [{:keys [workspace-id tr kind booking reverse? theme]}]
  (let [visits-conf? (<sub [::inspection-sub/visit-configuration-enabled?])]
    [:<>
     [hidden-help (util/mmap tr workspace-id)]
     [ui-table/modern-responsive-table
      (util/mmap
        {:cols              [[:inspection/inspection
                              #(inspection-model/inspection-name % (<sub [:language]))
                              {:truncate? true}]
                             [:inspection/current-status
                              (partial inspection-status tr kind visits-conf?)
                              {:class     "uppercase"
                               :truncate? true
                               :width     (if (= kind :wide) "35%" "8rem")}]
                             (when (= kind :wide)
                               [:inspection/visit-count
                                (comp count-if-some inspection-visits)
                                {:width "4rem"}])
                             [:inspection/last-visit
                              last-visit
                              {:width "14rem"}]]
         :get-status        (comp (partial get-status visits-conf?) inspection-visits)
         :get-focus?        (partial get-focus?
                                     (-> booking :entity/parent :db/id))
         :empty-placeholder :inspection/no-inspections
         :items             (cond-> (<sub [::inspections workspace-id])
                              reverse? reverse)
         :link              {:make-href   inspection-href
                             :description (tr [:inspection/open])}}
        tr theme)]]))
